// StockistFY.jsx
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import XLSX from "xlsx-js-style";
import Loader from "../dist/Loader"; 

const StockistFY = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [stockists, setStockists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const getDefaultFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    return currentMonth >= 4
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`;
  };
  const [financialYear, setFinancialYear] = useState(getDefaultFinancialYear());
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState("");
  const generateFinancialYears = () => {
    const startYear = 2024;
    const today = new Date();
    const currentYear = today.getFullYear();
    const fyList = [];
    for (let year = startYear; year <= currentYear; year++) {
      fyList.push(`${year}-${year + 1}`);
    }
    setFinancialYearOptions(fyList);
  };
  useEffect(() => {
    generateFinancialYears();
    fetch(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => setStates([{ id: "All", name: "All" }, ...data]))
      .catch((err) => {
        console.error("Error fetching states:", err);
      });

    fetch(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => setDivisions([{ id: "All", name: "All" }, ...data]))
      .catch((err) => {
        console.error("Error fetching divisions:", err);
      });
  }, []);

  const handleGoClick = () => {
    setError("");
    setIsLoading(true);
    setShowResults(true);
    const params = new URLSearchParams();
    if (financialYear) {
      params.append("financial_year", financialYear);
    }
    if (selectedState && selectedState !== "All") {
      params.append("state_id", selectedState);
    }
    if (selectedDivision && selectedDivision !== "All") {
      params.append("division_id", selectedDivision);
    }

    const url = `${BASE_URL}/api/pob-targets/stockist-monthly-summary?${params.toString()}`;

    fetch(url, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setStockists(data);
        } else {
          console.error("Unexpected API response:", data);
          setStockists([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching stockists:", error);
        setStockists([]);
        setError("Failed to fetch data. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const formatDateSafe = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const handlePrint = () => {
    const headerCols = [
      "S.No",
      "Stockist Code",
      "State",
      "City",
      "Stockist",
      "Phone",
      "Added Date",
      "Apr SOB",
      "May SOB",
      "Jun SOB",
      "Jul SOB",
      "Aug SOB",
      "Sep SOB",
      "Oct SOB",
      "Nov SOB",
      "Dec SOB",
      "Jan SOB",
      "Feb SOB",
      "Mar SOB",
      "Total SOB",
    ];

    const rowsHtml =
      stockists && stockists.length
        ? stockists
          .map((s, idx) => {
            const monthCols = [
              s.apr_sob || 0,
              s.may_sob || 0,
              s.jun_sob || 0,
              s.jul_sob || 0,
              s.aug_sob || 0,
              s.sep_sob || 0,
              s.oct_sob || 0,
              s.nov_sob || 0,
              s.dec_sob || 0,
              s.jan_sob || 0,
              s.feb_sob || 0,
              s.mar_sob || 0,
            ]
              .map(
                (v) =>
                  `<td style="padding:6px;border:1px solid #ccc;text-align:right">${v}</td>`
              )
              .join("");

            return `<tr>
                <td style="padding:6px;border:1px solid #ccc;text-align:center">${idx + 1
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${s.stockist_code || ""
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${s.state_name || ""
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${s.city_name || ""
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${s.stockist_name || ""
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${s.mobile || ""
              }</td>
                <td style="padding:6px;border:1px solid #ccc">${formatDateSafe(
                s.added_date
              )}</td>
                ${monthCols}
                <td style="padding:6px;border:1px solid #ccc;text-align:right">${s.total_sob || 0
              }</td>
              </tr>`;
          })
          .join("")
        : `<tr><td colspan="${headerCols.length}" style="padding:12px;text-align:center">No stockists found</td></tr>`;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      alert("Please allow popups to print.");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>Stockist FY Report - ${financialYear || "All"}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial; padding:16px; }
            table { width:100%; border-collapse: collapse; font-size:12px; }
            th, td { border:1px solid #ccc; padding:6px; vertical-align: top; }
            thead th { background:#f2f7fb; font-weight:600; text-align:center; }
            h2 { margin:0 0 12px 0; }
          </style>
        </head>
        <body>
          <h2>Stockist FY Report - ${financialYear || "All"}</h2>
          <div style="margin-bottom:8px;">Filters: State=${selectedState}, Division=${selectedDivision}</div>
          <table>
            <thead><tr>${headerCols
        .map((c) => `<th>${c}</th>`)
        .join("")}</tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div style="margin-top:12px;font-size:12px">Generated on: ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try {
        printWindow.print();
      } catch (e) {
        console.error("Print error", e);
      }
    }, 400);
  };

  // EXPORT handler - XLSX
const handleExportExcel = () => {
    if (!stockists || !stockists.length) {
      alert("No data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const ws = {};
      const headers = [
        "S.No",
        "Stockist Code",
        "State",
        "City",
        "Stockist",
        "Phone",
        "Added Date",
        "Apr SOB",
        "May SOB",
        "Jun SOB",
        "Jul SOB",
        "Aug SOB",
        "Sep SOB",
        "Oct SOB",
        "Nov SOB",
        "Dec SOB",
        "Jan SOB",
        "Feb SOB",
        "Mar SOB",
        "Total SOB",
      ];
      const headerStyle = {
        font: {
          bold: true,
          color: { rgb: "FFFFFF" },
          sz: 11,
        },
        fill: {
          fgColor: { rgb: "2563EB" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const rowStyle = {
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      // ✅ NEW: real Excel date cell setter
      const setDateCell = (r, c, rawVal, s) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (rawVal) {
          try {
            const date = new Date(
              /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
                ? `${rawVal}T00:00:00`
                : rawVal
            );
            if (!isNaN(date.getTime())) {
              ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
              return;
            }
          } catch {}
        }
        ws[addr] = { v: "", t: "s", s };
      };

      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: 0,
          c: colIndex,
        });

        ws[cellAddress] = {
          v: header,
          t: "s",
          s: headerStyle,
        };
      });

      stockists.forEach((s, index) => {
        const excelRow = index + 2;

        const codeRaw = String(s.stockist_code ?? "").trim();
        const codeIsNum = /^\d+$/.test(codeRaw);
        const stockistCode = codeIsNum ? Number(codeRaw) : codeRaw;

        const rowData = [
          index + 1,
          stockistCode,
          s.state_name || "",
          s.city_name || "",
          s.stockist_name || "",
          s.mobile || "",
          null, // col 6 — Added Date (patched below)
          Number(s.apr_sob || 0),
          Number(s.may_sob || 0),
          Number(s.jun_sob || 0),
          Number(s.jul_sob || 0),
          Number(s.aug_sob || 0),
          Number(s.sep_sob || 0),
          Number(s.oct_sob || 0),
          Number(s.nov_sob || 0),
          Number(s.dec_sob || 0),
          Number(s.jan_sob || 0),
          Number(s.feb_sob || 0),
          Number(s.mar_sob || 0),
          0,
        ];

        rowData.forEach((value, colIndex) => {
          // ✅ Skip date col — patched below
          if (colIndex === 6) return;

          const cellAddress = XLSX.utils.encode_cell({
            r: index + 1,
            c: colIndex,
          });

          ws[cellAddress] = {
            v: value,
            t:
              colIndex === 1
                ? codeIsNum
                  ? "n"
                  : "s"
                : typeof value === "number"
                ? "n"
                : "s",
            s: rowStyle,
          };
        });

        // ✅ Patch Added Date as real Excel date
        setDateCell(index + 1, 6, s.added_date, rowStyle);

        ws[`T${excelRow}`] = {
          f: `SUM(H${excelRow}:S${excelRow})`,
          t: "n",
          s: rowStyle,
        };
      });

      const lastDataRow = stockists.length + 1;
      const footerRowIndex = stockists.length + 1;
      const footerStyle = {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: "DBEAFE" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
      ws[XLSX.utils.encode_cell({ r: footerRowIndex, c: 0 })] = {
        v: "Total",
        t: "s",
        s: footerStyle,
      };
      for (let c = 1; c <= 6; c++) {
        ws[XLSX.utils.encode_cell({ r: footerRowIndex, c })] = {
          v: "",
          t: "s",
          s: footerStyle,
        };
      }
      const colLetters = ["H","I","J","K","L","M","N","O","P","Q","R","S"];
      colLetters.forEach((col, i) => {
        ws[XLSX.utils.encode_cell({ r: footerRowIndex, c: 7 + i })] = {
          f: `SUM(${col}2:${col}${lastDataRow})`,
          t: "n",
          s: footerStyle,
        };
      });
      ws[XLSX.utils.encode_cell({ r: footerRowIndex, c: 19 })] = {
        f: `SUM(T2:T${lastDataRow})`,
        t: "n",
        s: footerStyle,
      };
      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({
        s: { r: footerRowIndex, c: 0 },
        e: { r: footerRowIndex, c: 6 },
      });
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: {
          r: footerRowIndex,
          c: headers.length - 1,
        },
      });
      ws["!cols"] = [
        { wch: 8 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 30 },
        { wch: 16 },
        { wch: 24 },
        ...Array.from({ length: 13 }, () => ({ wch: 12 })),
      ];

      ws["!freeze"] = {
        xSplit: 0,
        ySplit: 1,
      };

      ws["!autofilter"] = {
        ref: "A1:T1",
      };

      XLSX.utils.book_append_sheet(wb, ws, "Stockist FY Report");

      const safe = (s) =>
        String(s || "").replace(/[^a-z0-9_\-]/gi, "_");

      const fileName = `Stockist_FY_Report_${safe(financialYear)}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      XLSX.writeFile(wb, fileName, { compression: true });

    } catch (err) {
      console.error(err);
      alert("Failed to export Excel");
    }
  };

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Stockist</span>{" "}
          <span className="text-blue-500">FY</span>
        </h1>

        {/* Filters Section - responsive grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <label className="text-right text-sm text-gray-700">State</label>
          <select
            className="w-full p-2 border rounded text-sm"
            onChange={(e) => setSelectedState(e.target.value)}
            value={selectedState}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right text-sm text-gray-700">Division</label>
          <select
            className="w-full p-2 border rounded text-sm"
            onChange={(e) => setSelectedDivision(e.target.value)}
            value={selectedDivision}
          >
            {divisions.map((div) => (
              <option key={div.id} value={div.id}>
                {div.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center mt-4">
          <label className="text-right text-sm text-gray-700">
            Financial Year
          </label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            disabled={isLoading}
          >
            {financialYearOptions.map((fy, idx) => (
              <option key={idx} value={fy}>
                {fy}
              </option>
            ))}
          </select>

          {/* spacer columns to preserve 5-column layout on md and up */}
          <div />
          <div />
          <div />
        </div>

        {/* Error message */}
        {error && (
          <div className="text-center text-red-600 mt-3 font-semibold">
            {error}
          </div>
        )}

        {/* GO Button */}
        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {/* Results Section */}
      {showResults && (
        <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50 mt-4">
          {isLoading ? (
            <div className="text-center p-4">Loading data...</div>
          ) : (
            <>
              <div className="border rounded shadow  table-wrapper">
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="text-center p-2 border">S.No</th>
                      <th className="text-center p-2 border">Stockist Code</th>
                      <th className="text-center p-2 border">State</th>
                      <th className="text-center p-2 border">City</th>
                      <th className="text-center p-2 border">Stockist</th>
                      <th className="text-center p-2 border">Phone</th>
                      <th className="text-center p-2 border">Added Date</th>
                      <th className="text-center p-2 border">Apr SOB</th>
                      <th className="text-center p-2 border">May SOB</th>
                      <th className="text-center p-2 border">Jun SOB</th>
                      <th className="text-center p-2 border">Jul SOB</th>
                      <th className="text-center p-2 border">Aug SOB</th>
                      <th className="text-center p-2 border">Sep SOB</th>
                      <th className="text-center p-2 border">Oct SOB</th>
                      <th className="text-center p-2 border">Nov SOB</th>
                      <th className="text-center p-2 border">Dec SOB</th>
                      <th className="text-center p-2 border">Jan SOB</th>
                      <th className="text-center p-2 border">Feb SOB</th>
                      <th className="text-center p-2 border">Mar SOB</th>
                      <th className="text-center p-2 border">Total SOB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockists.length > 0 ? (
                      stockists.map((stockist, index) => (
                        <tr
                          key={stockist.stockist_code || index}
                          className={
                            index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }
                        >
                          <td className="text-left p-2 border">{index + 1}</td>
                          <td className="text-left p-2 border">
                            {stockist.stockist_code}
                          </td>
                          <td className="text-left p-2 border">
                            {stockist.state_name || "N/A"}
                          </td>
                          <td className="text-left p-2 border">
                            {stockist.city_name || "N/A"}
                          </td>
                          <td className="text-left p-2 border">
                            {stockist.stockist_name}
                          </td>
                          <td className="text-left p-2 border">
                            {stockist.mobile || "N/A"}
                          </td>
                          <td className="text-left p-2 border">
                            {formatDateSafe(stockist.added_date)}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.apr_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.may_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.jun_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.jul_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.aug_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.sep_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.oct_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.nov_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.dec_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.jan_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.feb_sob || 0}
                          </td>
                          <td className="text-center p-2 border">
                            {stockist.mar_sob || 0}
                          </td>
                          <td className="text-center p-2 border font-semibold">
                            {stockist.total_sob || 0}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={20} className="text-center p-4 border">
                          No stockists found for the selected filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                   {/* ===== FOOTER ROW ===== */}
                  <tfoot>
                    <tr className="bg-blue-600 font-bold text-white">
                      <td colSpan={7} className="text-center p-2 border font-bold">
                        Total
                      </td>
                      {[
                        "apr_sob","may_sob","jun_sob","jul_sob","aug_sob","sep_sob",
                        "oct_sob","nov_sob","dec_sob","jan_sob","feb_sob","mar_sob",
                      ].map((key) => (
                        <td key={key} className="text-center p-2 border font-bold">
                          {stockists.reduce((sum, s) => sum + Number(s[key] || 0), 0)}
                        </td>
                      ))}
                      <td className="text-center p-2 border font-bold">
                        {stockists.reduce((sum, s) => sum + Number(s.total_sob || 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                  {/* ===== END FOOTER ROW ===== */}
                </table>
              </div>

              {/* Print & Export buttons using CustomButton */}
              <div className="mt-4 flex gap-4 justify-center">
                <CustomButton onClick={handlePrint} label="Print" variant="print">
                  🖨
                </CustomButton>
                <CustomButton
                  onClick={handleExportExcel}
                  label="Export"
                  variant="export"
                >
                  📥
                </CustomButton>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StockistFY;
